import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { HorizontalLayout, VerticalLayout } from 'widgets/Layout';

export interface FormMultiSelectOption {
    label: string;
    value: string;
}

interface FormMultiSelectProps {
    name: string;
    options: FormMultiSelectOption[];
    searchTerm?: string;
}

export const FormMultiSelect = ({ name, options, searchTerm = '' }: FormMultiSelectProps) => {
    const { register } = useFormContext();

    const filteredOptions: { option: FormMultiSelectOption; isFilterActive: boolean }[] = useMemo(
        () =>
            options.map((option) => ({
                option,
                isFilterActive: option.label.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
            })),
        [options, searchTerm],
    );

    return (
        <OptionsWrapper $gap={8}>
            {filteredOptions.map(({ option, isFilterActive }) => (
                <Option key={option.value} $apart $isFilterActive={isFilterActive}>
                    <Input type="checkbox" {...register(name)} value={option.value} id={option.value} />
                    <Label htmlFor={option.value}>{option.label}</Label>
                </Option>
            ))}
        </OptionsWrapper>
    );
};

const OptionsWrapper = styled(VerticalLayout)`
    overflow-y: auto;
    max-height: 100%;
`;

// `display: none;` to keep elements attached in DOM when selected and filtered
const Option = styled(HorizontalLayout)<{ $isFilterActive: boolean }>`
    ${({ $isFilterActive }) => ($isFilterActive ? '' : `display: none;`)}
    border: 1px solid ${({ theme }) => theme.colors.cocoa200};
    border-radius: 5px;
    flex-direction: row-reverse;
    flex: 1;

    &:hover {
        background-color: ${({ theme }) => theme.colors.cocoa0};
    }
`;

const Label = styled.label`
    padding: 8px;
    width: 100%;
    cursor: pointer;
`;

const Input = styled.input`
    margin-right: 8px;
    cursor: pointer;
`;
